import i18n from "i18n";
import config from "common/config";
import hmacSHA256 from 'crypto-js/hmac-sha256';
import Hex from 'crypto-js/enc-hex';
import { isEmpty } from "lodash";

export const initInbox = () => {
  // console.log('in initInbox: window.$chatwoot is undefined', window.$chatwoot === undefined);
  if (window.$chatwoot) {
    resetChatwoot();
    showChatwoot();
  } else {
    initChatwoot();
  }
}

const isMobile = () => window.matchMedia('(max-width: 999px)').matches;

const initChatwoot = () => {
  // On phones and tablets the expanded bubble sits over the primary button of
  // the signup steps, so it moves to the opposite corner and drops the label.
  const mobile = isMobile();
  window.chatwootSettings = {
    hideMessageBubble: false,
    position: mobile ? 'left' : 'right', // This can be left or right
    locale: 'en', // Language to be set
    type: mobile ? 'standard' : 'expanded_bubble', // [standard, expanded_bubble]
    launcherTitle: i18n.t('helpCenterLauncherTitle')
  };
  window.chatwootSDK.run({
    websiteToken: config.CHATWOOT_TOKEN,
    baseUrl: config.CHATWOOT_BASEURL
  });
  if (mobile) makeBubbleDraggable();
}

// On the small layout the bubble can still land on something the user needs,
// so it can be dragged out of the way.
const makeBubbleDraggable = () => {
  const attach = (bubble) => {
    const DRAG_THRESHOLD = 5;
    let dragging = false;
    let offsetX = 0;
    let offsetY = 0;
    let startX = 0;
    let startY = 0;

    bubble.style.touchAction = 'none';

    bubble.addEventListener('pointerdown', (e) => {
      const rect = bubble.getBoundingClientRect();
      offsetX = e.clientX - rect.left;
      offsetY = e.clientY - rect.top;
      startX = e.clientX;
      startY = e.clientY;
      dragging = false;
      bubble.setPointerCapture(e.pointerId);
    });

    bubble.addEventListener('pointermove', (e) => {
      if (!bubble.hasPointerCapture(e.pointerId)) return;
      const rect = bubble.getBoundingClientRect();
      if (!dragging) {
        // A tap is never perfectly still, so small wobble must not count.
        if (Math.abs(e.clientX - startX) < DRAG_THRESHOLD &&
            Math.abs(e.clientY - startY) < DRAG_THRESHOLD) return;
        // Held back until then: a plain tap must not drop the corner anchor,
        // which would leave the bubble below the fold.
        bubble.style.right = 'auto';
        bubble.style.bottom = 'auto';
        dragging = true;
      }
      const x = Math.min(Math.max(e.clientX - offsetX, 0), window.innerWidth - rect.width);
      const y = Math.min(Math.max(e.clientY - offsetY, 0), window.innerHeight - rect.height);
      bubble.style.left = `${x}px`;
      bubble.style.top = `${y}px`;
    });

    bubble.addEventListener('click', (e) => {
      // A drag ends over the bubble, which would otherwise open the chat.
      if (dragging) {
        e.stopPropagation();
        e.preventDefault();
        dragging = false;
        return;
      }
      // The panel and its close button stay in their corner, so the launcher
      // returns there rather than sitting apart from the chat it opened.
      bubble.style.left = '';
      bubble.style.top = '';
      bubble.style.right = '';
      bubble.style.bottom = '';
    }, true);
  };

  const start = Date.now();
  const poll = setInterval(() => {
    // The holder is a static, zero-height wrapper; the bubble button is the
    // element Chatwoot positions, so that is the one to move.
    const bubble = document.querySelector('.woot-widget-bubble');
    if (bubble) {
      clearInterval(poll);
      attach(bubble);
    } else if (Date.now() - start > 15000) {
      clearInterval(poll);
    }
  }, 300);
}

export const resetChatwoot = () => {
  window.$chatwoot && window.$chatwoot.reset();
}

const getBubbleHolderElement = () => {
  return document.getElementsByClassName("woot--bubble-holder");
}

const hideChatwoot = () => {
  const bubbleHolderElement = getBubbleHolderElement();
  if (!isEmpty(bubbleHolderElement)) bubbleHolderElement[0].style.visibility = 'hidden';
}

const showChatwoot = () => {
  const bubbleHolderElement = getBubbleHolderElement();
  if (!isEmpty(bubbleHolderElement)) bubbleHolderElement[0].style.visibility = 'visible';
}

export const removeChatwootWidget = () => {
  resetChatwoot();
  hideChatwoot();
}

// Opens the support widget from a link elsewhere in the app, rather than making
// the reader hunt for the bubble in the corner.
export const openChatwoot = () => {
  window.$chatwoot && window.$chatwoot.toggle('open');
};

export const setUserProperties = (profile) => {
  const hash = hmacSHA256(profile.id, config.CHATWOOT_HASH_KEY).toString(Hex);
  if (window.$chatwoot) {
    window.$chatwoot.setUser(profile.id, {email: profile.email, name: profile.displayName, identifier_hash: hash});
    window.$chatwoot.setCustomAttributes({role: profile.role, language: profile.newPrimaryLanguage});
  }
};
